import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { buildHeatmapCanvas, drawHeatmap } from '../../utils/drawHeatmap';

/**
 * HeatmapOverlay - Jahresübersicht-Heatmap auf dem Fabric-Canvas (#606)
 *
 * Zeichnet das Sonnenstunden-Grid als weichgezeichnetes Farbfeld über den
 * Plan (via `after:render`). Das Offscreen-Bild wird pro Grid einmal gebaut und
 * dann nur skaliert gezeichnet — ein drawImage pro Frame.
 *
 * Wie ShadowOverlay: der Handler wird EINMAL registriert und liest Grid/Bild aus
 * Refs — kein Ab-/Anmelden pro Datenänderung (verhindert hängende Doppelbilder
 * während Pan/Zoom).
 */
const HeatmapOverlay = ({ canvas, grid, pixelsPerCm }) => {
  const image = useMemo(() => buildHeatmapCanvas(grid), [grid]);
  const gridRef = useRef(grid);
  const imageRef = useRef(image);

  useEffect(() => {
    if (!canvas) return undefined;

    const draw = ({ ctx }) => {
      // Nur auf den unteren Canvas malen — renderTop() feuert after:render mit
      // dem oberen (Auswahl-)Canvas, wo das Bild sonst eingefroren hängenbliebe.
      if (ctx === canvas.contextTop) return;
      drawHeatmap(ctx, canvas, gridRef.current, imageRef.current, { pixelsPerCm });
    };

    canvas.on('after:render', draw);
    canvas.requestRenderAll();

    return () => {
      canvas.off('after:render', draw);
      canvas.requestRenderAll();
    };
  }, [canvas, pixelsPerCm]);

  useEffect(() => {
    gridRef.current = grid;
    imageRef.current = image;
    canvas?.requestRenderAll();
  }, [canvas, grid, image]);

  return null;
};

HeatmapOverlay.propTypes = {
  canvas: PropTypes.object,
  grid: PropTypes.object,
  pixelsPerCm: PropTypes.number,
};

HeatmapOverlay.defaultProps = {
  canvas: null,
  grid: null,
  pixelsPerCm: 1,
};

export default HeatmapOverlay;
